import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type KbRunDetail, type ReportOutcome, type RunReport, type RunReportSection } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * The run report: what happened to each part of what was sent, readable in a minute.
 *
 * The other tabs are the run as a debugger sees it — the trail, the changes, placement's reasoning,
 * the items. This is the run as the person who sent the material asks about it: did it land, where,
 * what was left out and why, and is there anything I need to do. Outcomes are worked out from what the
 * run recorded (citations, the words it added to pages, placement's cut); reasons are what the agents
 * said. See packages/knowledge-base/src/ingest/report.ts.
 */

const OUTCOME: Record<ReportOutcome, { label: string; tone: string }> = {
  kept: { label: 'Kept', tone: 'bg-success-bg text-success-text' },
  condensed: { label: 'Condensed', tone: 'bg-warning-bg text-warning-text' },
  dropped: { label: 'Dropped', tone: 'bg-danger-bg text-danger-text' },
  'set-aside': { label: 'Set aside', tone: 'bg-surface-muted text-ink-secondary' },
  withheld: { label: 'Withheld', tone: 'bg-surface-muted text-ink-secondary' },
  known: { label: 'Already in memory', tone: 'bg-info-bg text-info-text' },
};

export function RunReportPanel({
  run,
  projectId,
  onOpenNode,
  onOpenChanges,
}: {
  run: KbRunDetail;
  projectId: string;
  onOpenNode: (path: string, changedProjectId: string) => void;
  onOpenChanges: () => void;
}) {
  const [report, setReport] = useState<RunReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyLooks, setOnlyLooks] = useState(false);
  const settled = run.status === 'COMPLETED' || run.status === 'FAILED';

  useEffect(() => {
    setReport(null);
    setError(null);
    if (!settled) return;
    let cancelled = false;
    api.kb
      .runReport(projectId, run.id)
      .then((next) => !cancelled && setReport(next))
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [projectId, run.id, settled]);

  if (!settled) {
    return <Muted>The report is ready when the run finishes. The Run tab shows it working.</Muted>;
  }
  if (error) return <p className="text-xs text-danger-text">{error}</p>;
  if (!report) return <Muted>Putting the report together…</Muted>;

  const needsLook = (s: RunReportSection) => s.outcome === 'condensed' || s.outcome === 'dropped';
  const sections = onlyLooks ? report.sections.filter(needsLook) : report.sections;
  const lookCount = report.sections.filter(needsLook).length;

  return (
    <div className="space-y-5">
      <p className="text-[15px] font-semibold leading-snug text-ink">{report.summary}</p>

      <Timing report={report} />
      <SizeBar size={report.size} />
      <Attention report={report} onOpenChanges={onOpenChanges} />

      {report.notRecorded.length > 0 && (
        <ul className="space-y-0.5">
          {report.notRecorded.map((note) => (
            <li key={note} className="text-[11px] text-ink-faint">
              {note}
            </li>
          ))}
        </ul>
      )}

      {report.sections.length > 0 && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold text-ink">What happened to each section</h3>
            {lookCount > 0 && lookCount < report.sections.length && (
              <div className="flex gap-1">
                <Chip on={!onlyLooks} onClick={() => setOnlyLooks(false)}>
                  All {report.sections.length}
                </Chip>
                <Chip on={onlyLooks} onClick={() => setOnlyLooks(true)}>
                  Condensed or dropped {lookCount}
                </Chip>
              </div>
            )}
          </div>
          <Sections run={run} projectId={projectId} report={report} sections={sections} onOpenNode={onOpenNode} />
        </section>
      )}
    </div>
  );
}

function Timing({ report }: { report: RunReport }) {
  const t = report.timing;
  const cells: Array<[string, string]> = [
    [clock(t.queuedAt), 'Queued'],
    [t.startedAt ? clock(t.startedAt) : '—', t.waitedSeconds !== null ? `Started · waited ${duration(t.waitedSeconds)}` : 'Started'],
    [t.finishedAt ? clock(t.finishedAt) : '—', 'Finished'],
    [t.seconds !== null ? duration(t.seconds) : '—', 'Took'],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 rounded-lg border border-border">
      {cells.map(([value, label], i) => (
        <div key={label} className={cn('px-3 py-2', i < 3 && 'sm:border-r border-border')}>
          <div className="text-[13px] font-semibold tabular-nums text-ink">{value}</div>
          <div className="text-[10.5px] text-ink-faint">{label}</div>
        </div>
      ))}
    </div>
  );
}

function SizeBar({ size }: { size: RunReport['size'] }) {
  const parts: Array<[number, string, string]> = [
    [size.kept, 'bg-success-text', 'kept on pages'],
    [size.known, 'bg-info-text', 'already in memory'],
    [size.notWritten, 'bg-warning-text', 'not written'],
    [size.setAside, 'bg-ink-faint', 'set aside'],
    [size.withheld, 'bg-ink-secondary', 'withheld'],
  ];
  const total = Math.max(parts.reduce((n, [v]) => n + v, 0), 1);
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-[11px] text-ink-faint">
        <span className="font-semibold text-ink-secondary">Size</span>
        <span>
          {size.received.toLocaleString()} characters received · {size.kept.toLocaleString()} kept ({size.cited.toLocaleString()} cited
          word for word)
        </span>
      </div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-surface-muted">
        {parts.map(([value, tone, label]) =>
          value > 0 ? <span key={label} className={tone} style={{ width: `${(100 * value) / total}%` }} /> : null,
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-ink-faint">
        {parts.map(([value, tone, label]) =>
          value > 0 ? (
            <span key={label} className="inline-flex items-center gap-1">
              <i className={cn('inline-block size-2 rounded-sm', tone)} />
              {value.toLocaleString()} {label}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

function Attention({ report, onOpenChanges }: { report: RunReport; onOpenChanges: () => void }) {
  const unexplained = report.sections.filter((s) => s.outcome === 'dropped' && !s.reason).length;
  if (report.flags.length === 0 && report.rejectedCitations.length === 0 && unexplained === 0) {
    return <p className="rounded-lg bg-success-bg px-3 py-2 text-xs text-success-text">Nothing needs your attention.</p>;
  }
  return (
    <section className="overflow-hidden rounded-lg border border-border">
      <h3 className="border-b border-border bg-surface-subtle px-3 py-2 text-xs font-semibold text-ink">Needs your attention</h3>
      {report.flags.map((flag) => (
        <Row key={flag.changeId}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex flex-wrap items-center gap-1.5">
              <Pill tone="bg-danger-bg text-danger-text">
                Flag · {flag.reason === 'sensitive' ? 'Sensitive' : flag.reason === 'conflict' ? 'Conflict' : 'Unsure'}
              </Pill>
              <Path>{`${flag.projectName} › ${flag.path}`}</Path>
              {flag.reviewStatus !== 'PENDING' && <span className="text-[10.5px] text-ink-faint">{flag.reviewStatus.toLowerCase()}</span>}
            </span>
            {flag.reviewStatus === 'PENDING' && (
              <button onClick={onOpenChanges} className="rounded-md bg-ink px-2.5 py-1 text-[11px] font-medium text-surface">
                Review
              </button>
            )}
          </div>
          <p className="text-[11.5px] text-ink-secondary">{flag.trigger}</p>
          {flag.quote && <Quote>“{flag.quote}”</Quote>}
        </Row>
      ))}
      {report.rejectedCitations.map((c, i) => (
        <Row key={`${c.path}-${i}`}>
          <span className="flex flex-wrap items-center gap-1.5">
            <Pill tone="bg-warning-bg text-warning-text">Citation not recorded</Pill>
            {c.path && <Path>{c.path}</Path>}
          </span>
          <p className="text-[11.5px] text-ink-secondary">
            Claim: “{c.claim}”. {c.reason}
          </p>
          {c.closest && <Quote dashed>Closest passage in the source: “{c.closest}”</Quote>}
        </Row>
      ))}
      {unexplained > 0 && (
        <Row>
          <p className="text-[11.5px] text-ink-secondary">
            {unexplained} section{unexplained === 1 ? ' was' : 's were'} dropped with no reason given. They are marked below.
          </p>
        </Row>
      )}
    </section>
  );
}

function Sections({
  run,
  projectId,
  report,
  sections,
  onOpenNode,
}: {
  run: KbRunDetail;
  projectId: string;
  report: RunReport;
  sections: RunReportSection[];
  onOpenNode: (path: string, changedProjectId: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const grouped = report.counts.items > 1;
  const groups = useMemo(() => {
    const map = new Map<number, RunReportSection[]>();
    for (const s of sections) map.set(s.itemIndex, [...(map.get(s.itemIndex) ?? []), s]);
    return [...map.entries()];
  }, [sections]);

  return (
    <div className="space-y-2">
      {groups.map(([itemIndex, rows]) => (
        <div key={itemIndex} className="overflow-hidden rounded-lg border border-border">
          {grouped && (
            <div className="border-b border-border bg-surface-subtle px-3 py-2">
              <div className="text-xs font-semibold text-ink">{rows[0]?.itemTitle ?? run.items[itemIndex - 1]?.title ?? `Item ${itemIndex}`}</div>
              <div className="text-[10.5px] text-ink-faint">{itemSummary(report.sections.filter((s) => s.itemIndex === itemIndex))}</div>
            </div>
          )}
          <table className="w-full text-xs">
            <tbody>
              {rows.map((s) => {
                const key = `${s.itemIndex}:${s.start}`;
                const isOpen = open === key;
                return (
                  <Fragment key={key}>
                    <tr
                      onClick={() => setOpen(isOpen ? null : key)}
                      className={cn('cursor-pointer border-b border-border-subtle align-top last:border-0 hover:bg-surface-subtle', isOpen && 'bg-surface-subtle')}
                    >
                      <td className="px-3 py-2 text-ink">{s.heading ?? <span className="text-ink-faint">(opening)</span>}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-ink-faint">{s.chars.toLocaleString()}</td>
                      <td className="whitespace-nowrap px-2 py-2">
                        <Pill tone={OUTCOME[s.outcome].tone}>
                          {OUTCOME[s.outcome].label}
                          {s.outcome === 'kept' && s.action ? ` · ${s.action}` : ''}
                        </Pill>
                      </td>
                      <td className="px-3 py-2">
                        {s.pages.length > 0 ? (
                          <span className="flex flex-col gap-0.5">
                            {s.pages.slice(0, 3).map((p) => (
                              <button
                                key={`${p.projectId}:${p.path}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenNode(p.path, p.projectId);
                                }}
                                className="truncate text-left font-mono text-[11px] text-info-text hover:underline"
                              >
                                {p.projectName} › {p.path}
                              </button>
                            ))}
                          </span>
                        ) : (
                          <span className="text-ink-faint">Nowhere</span>
                        )}
                        {s.reason ? (
                          <span className="mt-0.5 block text-[11px] text-ink-faint">
                            {s.reasonFrom === 'writer' ? 'Writer: ' : s.reasonFrom === 'placement' ? 'Set aside: ' : ''}
                            {s.reason}
                          </span>
                        ) : s.outcome === 'dropped' || s.outcome === 'condensed' ? (
                          <span className="mt-0.5 block text-[11px] text-warning-text">No reason given</span>
                        ) : null}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={4} className="border-b border-border-subtle bg-surface-subtle px-3 pb-3">
                          <SectionText run={run} projectId={projectId} section={s} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** The section as the source wrote it, with what landed highlighted. Fetched only when opened. */
function SectionText({ run, projectId, section }: { run: KbRunDetail; projectId: string; section: RunReportSection }) {
  const item = run.items[section.itemIndex - 1];
  const [content, setContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    api.kb
      .runItem(projectId, run.id, item.id)
      .then((detail) => !cancelled && setContent(detail.content))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [projectId, run.id, item]);

  if (!item || failed) return <Muted>The text of this section is no longer stored.</Muted>;
  if (content === null) return <Muted>Loading the section…</Muted>;

  const text = content.slice(section.start, section.end);
  return (
    <div className="space-y-1.5 pt-1">
      <div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-card px-3 py-2 text-[12px] leading-relaxed text-ink-secondary [overflow-wrap:anywhere]">
        {marked(text, section)}
      </div>
      <div className="flex flex-wrap gap-3 text-[10.5px] text-ink-faint">
        <span>
          <i className="mr-1 inline-block size-2 rounded-sm bg-success-text/40" />
          cited word for word
        </span>
        <span>
          <i className="mr-1 inline-block size-2 rounded-sm bg-success-text/15" />
          written in other words
        </span>
        <span>
          {section.citedSentences + section.writtenSentences} of {section.sentences} sentences written
          {section.knownSentences > 0 ? `, ${section.knownSentences} already in memory` : ''}
        </span>
      </div>
    </div>
  );
}

function marked(text: string, section: RunReportSection): ReactNode {
  const ranges = [
    ...section.cited.map((r) => ({ ...r, tone: 'bg-success-text/40' })),
    ...section.written.map((r) => ({ ...r, tone: 'bg-success-text/15' })),
  ]
    .map((r) => ({ start: r.start - section.start, end: r.end - section.start, tone: r.tone }))
    .filter((r) => r.end > 0 && r.start < text.length)
    .sort((a, b) => a.start - b.start);
  const nodes: ReactNode[] = [];
  let at = 0;
  ranges.forEach((r, i) => {
    const start = Math.max(r.start, at);
    if (start > at) nodes.push(text.slice(at, start));
    if (r.end > start) nodes.push(<mark key={i} className={cn('rounded-sm text-inherit', r.tone)}>{text.slice(start, r.end)}</mark>);
    at = Math.max(at, r.end);
  });
  if (at < text.length) nodes.push(text.slice(at));
  return nodes;
}

function itemSummary(sections: RunReportSection[]): string {
  const n = (o: ReportOutcome) => sections.filter((s) => s.outcome === o).length;
  const parts = [`${n('kept')} kept`, `${n('condensed')} condensed`, `${n('dropped') + n('set-aside')} dropped`];
  if (n('known')) parts.push(`${n('known')} already in memory`);
  return `${sections.length} section${sections.length === 1 ? '' : 's'} · ${parts.join(' · ')}`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function duration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-xs text-ink-faint">{children}</p>;
}

function Row({ children }: { children: ReactNode }) {
  return <div className="space-y-1 border-b border-border-subtle px-3 py-2.5 last:border-0">{children}</div>;
}

function Pill({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={cn('inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold', tone)}>{children}</span>;
}

function Path({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[11px] text-info-text">{children}</span>;
}

function Quote({ children, dashed }: { children: ReactNode; dashed?: boolean }) {
  return (
    <p className={cn('rounded-md px-2 py-1 text-[11.5px] text-ink-secondary', dashed ? 'border border-dashed border-border bg-surface-subtle' : 'bg-danger-bg')}>
      {children}
    </p>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-medium', on ? 'bg-ink text-surface' : 'bg-surface-muted text-ink-secondary hover:text-ink')}
    >
      {children}
    </button>
  );
}
